import { ANALYTICS_EVENT, ANALYTICS_PROPERTY } from '@kouto/types';
import { useAppState } from 'AppProvider';
import { getProductFromResourceGroups } from 'features/analytics/services/analytics.helpers';
import { usePageViewEvent } from 'hooks/usePageViewEvent';
import { useMemo } from 'react';
import type { Session } from 'types/listings';

/** What the product view reads of the listing and of the group, whatever shape the page holds them in. */
type ViewedListing = {
  category?: { name: string } | null;
  resourceGroupCollectionId?: string | null;
  title?: string | null;
};
type ViewedResourceGroup = Parameters<
  typeof getProductFromResourceGroups
>[0]['groups'][number];

interface UseGroupViewEventOptions {
  listing?: ViewedListing | null;
  resourceGroup?: ViewedResourceGroup | null;
  /** The session the page opened on; its tiers are what the product view prices. */
  session?: Session;
  /**
   * What each tier costs for the whole booking picked, by tier name, once the server has quoted it.
   * A group booked by start and length or by day has no price until then, so the event waits for it.
   */
  priceByTierName?: Record<string, number>;
  /** The group is booked by start and length or by day, so its price is the quoted booking's. */
  requiresQuotedPrice: boolean;
}

/**
 * The product view of a resource group's page.
 *
 * A flexible or multi-day group reports what each tier costs for the booking the page opened on, the
 * figure the guest sees. Currency and brand are read from the brand's settings and sent with it, not
 * left to the shared analytics data, which is only set after the brand's integrations have loaded —
 * after this event may otherwise be ready to go.
 */
export const useGroupViewEvent = ({
  listing,
  resourceGroup,
  requiresQuotedPrice,
  session,
  priceByTierName,
}: UseGroupViewEventOptions) => {
  const { settings } = useAppState(
    (state: Record<string, unknown>) => state.brand,
  );

  const sessionWithQuotedPrices = useMemo(
    () =>
      session && priceByTierName
        ? {
            ...session,
            priceTiers: session.priceTiers?.map((tier) => ({
              ...tier,
              price: priceByTierName[tier.name] ?? tier.price,
            })),
          }
        : session,
    [session, priceByTierName],
  );

  usePageViewEvent({
    eventName: ANALYTICS_EVENT.VIEW_EXPERIENCE_DETAIL,
    payload: {
      [ANALYTICS_PROPERTY.BrandName]: settings?.name,
      [ANALYTICS_PROPERTY.CurrencyCode]: settings?.currency,
      [ANALYTICS_PROPERTY.ExperienceId]: resourceGroup?.id,
      [ANALYTICS_PROPERTY.ExperienceTitle]:
        resourceGroup?.title ?? 'Title unavailable',
      [ANALYTICS_PROPERTY.Products]: JSON.stringify(
        getProductFromResourceGroups({
          groups: resourceGroup ? [resourceGroup] : [],
          categoryName: listing?.category?.name,
          session: sessionWithQuotedPrices,
        }),
      ),
    },
    optionalPayload: {
      [ANALYTICS_PROPERTY.ExperienceCategory]: listing?.category?.name,
      [ANALYTICS_PROPERTY.ListId]:
        listing?.resourceGroupCollectionId ?? undefined,
      [ANALYTICS_PROPERTY.ListName]: listing?.title ?? 'Title unavailable',
    },
    isNotReady:
      !resourceGroup?.id ||
      !settings?.name ||
      !settings?.currency ||
      (requiresQuotedPrice && !priceByTierName),
  });
};

export default useGroupViewEvent;
